import React, { FC, useEffect } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { NavLink } from "react-router-dom";

import { Input, Label, Button, CountrySelect, TextArea } from "../../common";
import { SUPPORT_EMAIL } from "../../../constants";
import { useChangeDefaultInvoiceDataMutation } from "../../../store/services/user";
import { toastSuccess } from "../../../utils/toasts";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../../store/reducers/user";
import { AccountType, InvoicingData } from "../../../types/UserTypes";

type Inputs = {
  company_name: string;
  tax_id: string;
  reg_number: string;
  address: string;
  country: string;
  city: string;
  postal_code: string;
};

type Props = {
  data?: InvoicingData;
};

export const InvoiceDetailsForm: FC<Props> = ({ data }) => {
  const {
    company_name = "",
    tax_id = "",
    reg_number = "",
    address = "",
    country = "",
    city = "",
    postal_code = "",
  } = data || {};

  const { account_type, kyc_complete_and_approved } =
    useSelector(selectCurrentUser);

  const {
    handleSubmit,
    control,
    formState: { isDirty, isValid },
  } = useForm<Inputs>({
    defaultValues: {
      company_name,
      tax_id,
      reg_number,
      address,
      country,
      postal_code,
      city,
    },
  });

  const companyNameFieldLabel =
    account_type === AccountType.CONTRACTOR
      ? "Company nickname"
      : "Registered business name";

  const companyNameFieldNote =
    account_type === AccountType.CONTRACTOR
      ? "Your personal name will still pop up as sole proprietorship."
      : "";

  const regNumberFieldNote =
    account_type === AccountType.CONTRACTOR
      ? "Business registration number, in some countries it matches Tax ID number. If you work as individual fill up the field with your personal ID number."
      : "Business registration number, in some countries it matches Tax ID number.";

  const taxIdFieldNote =
    account_type === AccountType.CONTRACTOR
      ? "Unique identifier for tax purposes. You can find your Tax ID on official tax documents. In some countries it matches your regular ID number."
      : "Unique identifier for tax purposes.";

  const addressFieldNote =
    account_type === AccountType.CONTRACTOR
      ? "Your residential address in the country you pay taxes in."
      : "";

  const [changeDefaultInvoiceData, { isSuccess, isLoading, error }] =
    useChangeDefaultInvoiceDataMutation();

  const onSubmit: SubmitHandler<Inputs> = async (formData) =>
    await changeDefaultInvoiceData(formData);

  useEffect(() => {
    if (isSuccess) {
      toastSuccess("Invoice details updated");
    }
  }, [isLoading, isSuccess]);

  const isVerifiedCompanyAccount =
    account_type === AccountType.BUSINESS && kyc_complete_and_approved;

  const verifiedPostfix =
    (isVerifiedCompanyAccount && (
      <span className="absolute -left-12">Verified</span>
    )) ||
    undefined;

  const verifiedPostfixAddress =
    (isVerifiedCompanyAccount && (
      <span className="absolute -left-12 -top-10">Verified</span>
    )) ||
    undefined;

  return (
    <div>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-y-12"
      >
        <div className="flex flex-col gap-y-8">
          <Label label={companyNameFieldLabel}>
            <Controller
              name="company_name"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  note={companyNameFieldNote}
                  error={(error as any)?.data?.invoicing_data?.company_name}
                  disabled={isVerifiedCompanyAccount}
                  postfix={verifiedPostfix}
                />
              )}
            />
          </Label>

          <Label label="Reg. №">
            <Controller
              name="reg_number"
              control={control}
              rules={{ required: account_type === AccountType.BUSINESS }}
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  note={
                    account_type === AccountType.CONTRACTOR
                      ? `Optional. ${regNumberFieldNote}`
                      : regNumberFieldNote
                  }
                  error={(error as any)?.data?.invoicing_data?.reg_number}
                  disabled={isVerifiedCompanyAccount}
                  postfix={verifiedPostfix}
                />
              )}
            />
          </Label>

          {isVerifiedCompanyAccount && (
            <div className="flex justify-between">
              <div className="theme-subheading">
                To change name after KYC please <br />
                contact customer support
              </div>

              <Button
                variant={Button.variant.SECONDARY}
                component={NavLink}
                to={`mailto:${SUPPORT_EMAIL}`}
              >
                Contact support
              </Button>
            </div>
          )}

          <Label label="Tax ID">
            <Controller
              name="tax_id"
              control={control}
              rules={{ required: account_type === AccountType.BUSINESS }}
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  note={
                    account_type === AccountType.CONTRACTOR
                      ? `Optional. ${taxIdFieldNote}`
                      : taxIdFieldNote
                  }
                  error={(error as any)?.data?.invoicing_data?.tax_id}
                />
              )}
            />
          </Label>

          <Label label="Address">
            <Controller
              name="address"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextArea
                  {...field}
                  rows={3}
                  note={addressFieldNote}
                  error={(error as any)?.data?.invoicing_data?.address}
                  disabled={isVerifiedCompanyAccount}
                  postfix={verifiedPostfixAddress}
                  className={isVerifiedCompanyAccount ? "pr-28" : ""}
                />
              )}
            />
          </Label>

          {!isVerifiedCompanyAccount && (
            <>
              <Label label="Postal code">
                <Controller
                  name="postal_code"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      error={(error as any)?.data?.invoicing_data?.postal_code}
                    />
                  )}
                />
              </Label>

              <Label label="City">
                <Controller
                  name="city"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      error={(error as any)?.data?.invoicing_data?.city}
                    />
                  )}
                />
              </Label>
            </>
          )}

          <Label label="Country">
            <Controller
              name="country"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <CountrySelect
                  error={(error as any)?.data?.invoicing_data?.country}
                  {...field}
                  disabled={isVerifiedCompanyAccount}
                  postfix={verifiedPostfix}
                  excludeBlacklisted={account_type === AccountType.CONTRACTOR}
                />
              )}
            />
          </Label>
        </div>

        <div className="flex flex-col items-center justify-between">
          <Button
            type="submit"
            variant={Button.variant.PRIMARY}
            className="w-full"
            disabled={!isDirty || !isValid}
          >
            Save
          </Button>

          <div className="text-base text-center text-gray-300 mt-6">
            Next time you can just duplicate your invoice so you don't have to
            fill up business information again
          </div>
        </div>
      </form>
    </div>
  );
};
